/**
 * 단품 상품을 만들 때 "예약시 받을 정보"에 주소 3종을 넣어 준다.
 *
 * 단품은 픽업·배송처럼 받는 곳이 있어야 하는 상품이 많아, 만들 때마다 사람이 기본설정에
 * 들어가 같은 필드 셋을 손으로 추가하고 있었다. 그 절차를 생성 직후 한 번에 처리한다.
 *
 * ── 서버 구조를 알아야 이 파일이 이해된다 ────────────────────────────
 * 예약시 받을 정보는 두 층이다. 상품마다 필드를 만드는 구조가 아니다.
 *
 *   DBO.필드            업체 단위 "필드 정의". 상품번호가 없다.
 *                       (공급업체번호 + 적용대상구분 016001, 상품종류 001002 로 묶임)
 *   DBO.예약정보필드     상품별 on/off. (상품번호 + 필드번호 + 표시여부)
 *
 * 그래서 하는 일이 둘로 나뉜다.
 *   1) 업체에 '우편번호/주소/상세주소' 정의가 없으면 만든다 — 업체당 한 번이면 된다.
 *      상품마다 만들면 같은 이름의 정의가 계속 쌓이고, 그 업체의 다른 상품 기본설정에도
 *      중복된 항목이 그만큼 늘어나 보인다. 그래서 이름으로 있는지 먼저 확인한다.
 *   2) 새로 만든 그 상품에서 세 개를 켠다.
 *
 * ⚠ 정의가 상품종류(001002) 단위라, 이 세 필드는 같은 업체의 투어 상품 기본설정 목록에도
 *   항목으로 나타난다. 켜져 있지 않을 뿐이다. 단품에만 보이게 하려면 상품종류를 나눠야 하고
 *   그건 서버 쪽 변경이다.
 */

import {
  getProductDetailExtra,
  insertRequestField,
  saveProductDetailExtra,
} from './apiService';

/** 단품에 자동으로 켜 줄 필드. 순서대로 만들어진다. */
export const SINGLE_ITEM_BOOKING_FIELD_NAMES = ['우편번호', '주소', '상세주소'];

/** 서버는 데이터타입을 'Text'/'List'/'Checkbox' 로 주고, 화면과 저장 API 는 한글을 쓴다. */
const TEXT_DATA_TYPE = '텍스트';

/** 조회 결과의 필드 이름은 화면용(필드라벨)과 원본(필드명)이 섞여 온다. */
const fieldLabel = (row) => String(row?.필드라벨 ?? row?.필드명 ?? '').trim();

/**
 * 단품 상품 하나에 주소 필드 3종을 붙인다.
 *
 * @param {number|string} productNo 방금 만든 상품번호
 * @returns {Promise<{added: string[], enabled: string[]}>} 새로 만든 정의와 이 상품에서 켠 필드
 */
export async function ensureSingleItemBookingFields(productNo) {
  const no = Number(productNo);
  if (!Number.isFinite(no)) throw new Error('상품번호가 없습니다.');

  const before = await getProductDetailExtra(no);
  const existingNames = new Set((before.requestFields || []).map(fieldLabel));

  // 1) 업체에 없는 정의만 만든다
  const missing = SINGLE_ITEM_BOOKING_FIELD_NAMES.filter((name) => !existingNames.has(name));
  for (const name of missing) {
    await insertRequestField({
      productTypeName: '투어', // 필드 정의는 상품종류 001002 하나로 묶인다
      field: {
        필드라벨: name,
        데이터타입: TEXT_DATA_TYPE,
        표시여부: true,
        필수여부: true,
        옵션목록: [],
      },
    });
  }

  // 2) 필드번호를 알아야 이 상품에서 켤 수 있다. 만들었으면 다시 읽는다.
  const after = missing.length > 0 ? await getProductDetailExtra(no) : before;
  const allFields = after.requestFields || [];

  const targets = new Set(SINGLE_ITEM_BOOKING_FIELD_NAMES);
  // 이미 켜져 있던 필드는 그대로 두고 우리 것만 더한다. 저장 SP 가 이 목록으로 상품의
  // 예약정보필드를 통째로 갈아끼우기 때문에, 빠뜨리면 켜져 있던 것이 꺼진다.
  const visible = allFields.filter((f) => !!f.표시여부 || targets.has(fieldLabel(f)));

  const enabled = visible.filter((f) => targets.has(fieldLabel(f))).map(fieldLabel);
  if (enabled.length === 0) {
    throw new Error('주소 필드를 찾지 못해 켜지 못했습니다.');
  }

  // 3) 상세정보는 읽은 그대로 돌려보낸다. 이 SP 는 @상세정보 로 상품 설정을 통째로
  //    UPDATE 하므로, 빈 객체를 보내면 대기예약허용여부 같은 값들이 날아간다.
  await saveProductDetailExtra({
    productNo: no,
    detail: after.detail || {},
    requestFields: visible.map((f) => ({ ...f, 필수여부: true })),
  });

  return { added: missing, enabled };
}
